import {
  Calendar,
  CalendarCheck,
  CheckSquare,
  Square,
} from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { ModuleType } from "../../../templates/types";
import type { Api } from "../../core/apis";
import { groupedRangeToDates } from "../../core/groupDates";
import type { Stores } from "../../core/stores";
import BaseModuleModel, {
  type MenuOption,
  type ModuleModelData,
  type Snapshot,
} from "../core/BaseModuleModel";
import { getDefaultSettingsForModule } from "../utils";
import {
  CalendarModuleBlockUI,
  CalendarModuleStaticUI,
  CalendarModuleUI,
  CalendarStaticModuleBlockUI,
} from "./CalendarModuleUI";
import CalendarItem from "./data/CalendarItemModel";

export type CalendarModuleSnapshot = {
  numParticipants: number;
};

export type CalendarModuleSettings = {
  minDate: Timestamp | null;
  maxDate: Timestamp | null;
  groupDays: boolean;
};

export interface CalendarModuleModelData
  extends ModuleModelData<CalendarModuleSnapshot> {
  selectedDates: string[] | null;
  settings: CalendarModuleSettings;
}

export default class CalendarModuleModel extends BaseModuleModel<
  CalendarModuleSnapshot,
  CalendarModuleModelData
> {
  constructor(stores: Stores, api: Api, data: CalendarModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      selectedDates: data.selectedDates ?? null,
      settings: getDefaultSettingsForModule(ModuleType.Calendar, data.settings),
    });
    if (this.data.settings.groupDays === undefined) {
      this.data.settings.groupDays = true;
    }

    makeObservable(this);
  }

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = this.snapshots;
  }

  @computed get snapshots() {
    const numParticipants = this.dataItems
      .filter((item): item is CalendarItem => {
        return CalendarItem.is(item);
      })
      .map((item) => {
        return item.data.userId;
      }).length;

    return { numParticipants };
  }

  @computed get userCalendarItem() {
    return this.dataItems.find((item): item is CalendarItem => {
      if (CalendarItem.is(item)) {
        return item.data.userId === this.plan.userInPlan?.uid;
      }

      return false;
    });
  }

  @computed get mostSelectedDate() {
    return this.calendarItems[0]?.[0];
  }

  @computed get calendarItems() {
    const dates = this.dataItems.reduce<{ [key: string]: string[] }>(
      (acc, item) => {
        if (CalendarItem.is(item)) {
          const userDisplayName = this.plan.users.find(
            (user) => user.uid === item.data.userId,
          )?.data.displayName;

          if (!userDisplayName) {
            return acc;
          }

          item.data.selectedDates.forEach((date) => {
            if (acc[date]) {
              acc[date].push(userDisplayName);
            } else {
              acc[date] = [userDisplayName];
            }
          });
        }

        return acc;
      },
      {},
    );

    return Object.entries(dates).sort((a, b) => b[1].length - a[1].length);
  }

  @computed get hasAddedDates(): boolean {
    return !!this.dataItems.find((item) => {
      return (
        CalendarItem.is(item) &&
        item.data.userId === this.userUid &&
        item.data.selectedDates.length
      );
    });
  }

  @computed get waitingForUsers() {
    return this.plan.users
      .filter(
        (user) =>
          !this.dataItems.some((item) => {
            return (
              CalendarItem.is(item) &&
              item.data.userId === user.uid &&
              item.data.selectedDates.length
            );
          }),
      )
      .map((user) => user.data.displayName);
  }

  @action
  setDone(selectedRanges: string[]) {
    this.doneFlowEnabled = false;
    const dates = selectedRanges.flatMap((date) => groupedRangeToDates(date));
    this.data.selectedDates = dates;
    this.updateSnapshotWithoutSave();

    this.toggleDoneState();
  }

  @action setDates(selectedRanges: string[]) {
    const dates = selectedRanges.flatMap((date) => groupedRangeToDates(date));
    this.data.selectedDates = dates;
    this.updateSnapshotWithoutSave();

    this.api.modulesApi.set(this);
  }

  toFireStore(): Omit<CalendarModuleModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      ...this.data,
    };
  }

  render() {
    return <CalendarModuleUI module={this} />;
  }

  renderStatic() {
    return <CalendarModuleStaticUI module={this} />;
  }

  renderBlock() {
    return <CalendarModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return <CalendarStaticModuleBlockUI module={this} />;
  }

  renderIcon(size?: number) {
    return <Calendar size={size} />;
  }

  @observable showSettingsFormModal = false;
  @action toggleSettingsFormModal() {
    this.showSettingsFormModal = !this.showSettingsFormModal;
    this.setActivitySheetOpen(!this.showSettingsFormModal);
  }

  @action async updateSettings(
    settings: Partial<CalendarModuleSettings>,
    skipSave?: boolean,
  ) {
    this.data.settings = { ...this.data.settings, ...settings };

    if (!skipSave) {
      await this.api.modulesApi.set(this);
      this.sendTelemetryEvent("settings_updated");
    }
  }

  renderMenuOptions(): MenuOption[] {
    return [
      {
        icon: CalendarCheck,
        label: "Set available date range",
        onPress: () => {
          runInAction(() => {
            this.toggleSettingsFormModal();
          });
        },
      },
      {
        icon: this.data.settings.groupDays ? CheckSquare : Square,
        label: "Group ranges",
        onPress: () => {
          this.updateSettings({
            groupDays: !this.data.settings.groupDays,
          });
        },
      },
    ];
  }
  @action setSettingsFormData(minDate: string, maxDate: string) {
    if (minDate) {
      this.data.settings.minDate = Timestamp.fromDate(new Date(minDate));
    } else {
      this.data.settings.minDate = null;
    }

    if (maxDate) {
      this.data.settings.maxDate = Timestamp.fromDate(new Date(maxDate));
    } else {
      this.data.settings.maxDate = null;
    }

    this.api.modulesApi.set(this);
  }

  static is(
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>,
  ): module is CalendarModuleModel {
    return module instanceof CalendarModuleModel;
  }
}
